import React from "react";
import AuthAdminUserModel from '../../code/models/AuthAdminUserModel';
import Toaster from "../Toaster";
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from "../CclDataTable";
import ApiPromotion from "../../code/api/ApiPromotion";
import Promotion from "../../code/models/Promotion";
import Api_Config from "../../code/api/ApiConfig";
import '../../css/adminpromotions.css';
import { CloseButton } from "reactstrap";
import ButtonConfirm, { ButtonConfirmSize } from "../ButtonConfirm";

interface IAdminPromotions {
    adminUser: AuthAdminUserModel;
}

export default function AdminPromotions({ adminUser }: IAdminPromotions) {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [promotions, setPromotions] = React.useState<Promotion[]>([]);
    const [selectedPromotion, setSelectedPromotion] = React.useState<Promotion | null>(null);
    const [editPromotion, setEditPromotion] = React.useState<Promotion | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);
    const [typeSelected, setTypeSelected] = React.useState<string>("z");
    const company = Api_Config.getSelectedCompany();

    // Format the money
    const moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: company.currencyCode });

    React.useEffect(() => {
        loadPromotions();
    }, []);

    async function loadPromotions() {
        setSelectedPromotion(null);
        setEditPromotion(null);
        ApiPromotion.getPromotions().then((proms) => {
            setPromotions(proms);
        });
    }

    function formatMoney(amount: number) {
        return moneyFormatter.format(amount);
    }

    const bplColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "code",
            title: "Code",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "description",
            title: "Description",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "type",
            title: "Type",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "amountDiscount",
            title: "Amount",
            visible: true,
            formatData: formatMoney,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "numberOfBidPeriods",
            title: "Months",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "activeStartDate",
            title: "Active Start",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "activeEndDate",
            title: "Active End",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];

    function rowSelected(rowData: Promotion): void {
        setSelectedPromotion(rowData);
        setEditPromotion(structuredClone(rowData));
        setAnyChanges(false);
    }

    function checkPromotionHasChanges() {
        var isAnyChanges = editPromotion !== null &&
            selectedPromotion !== null &&
            (selectedPromotion.code !== editPromotion.code ||
                selectedPromotion.description !== editPromotion.description ||
                selectedPromotion.type !== editPromotion.type ||
                selectedPromotion.amountDiscount !== editPromotion.amountDiscount ||
                selectedPromotion.numberOfBidPeriods !== editPromotion.numberOfBidPeriods ||
                selectedPromotion.activeStartDate !== editPromotion.activeStartDate ||
                selectedPromotion.activeEndDate !== editPromotion.activeEndDate);
        setAnyChanges(isAnyChanges);
        setTypeSelected(editPromotion?.type || "a");
    }

    function fromIsoDate(val: string) {
        return new Date(val);
    }

    function saveChanges() {
        if (anyChanges && !!editPromotion && !!selectedPromotion) {
            ApiPromotion.savePromotion(editPromotion).then(savedPromo => {
                var isNew = editPromotion.promotionId || 0 == 0;
                if (savedPromo) {
                    if (isNew) {
                        selectedPromotion.promotionId = savedPromo.promotionId;
                    }
                    selectedPromotion.code = savedPromo.code;
                    selectedPromotion.description = savedPromo.description;
                    selectedPromotion.type = savedPromo.type;
                    selectedPromotion.amountDiscount = savedPromo.amountDiscount;
                    selectedPromotion.numberOfBidPeriods = savedPromo.numberOfBidPeriods;
                    selectedPromotion.activeStartDate = savedPromo.activeStartDate;
                    selectedPromotion.activeEndDate = savedPromo.activeEndDate;

                    setToastMessage(`Promotion "${selectedPromotion.code}" Saved.`);

                    if (isNew) {
                        loadPromotions();
                    } else {
                        rowSelected(selectedPromotion);
                    }
                }
            });
        }
    }

    function deletePromotionClick(confirm: boolean): void {
        if (!selectedPromotion || !confirm) return;
        if (selectedPromotion.promotionId > 0) {
            ApiPromotion.deletePromotion(selectedPromotion.promotionId).then((success) => {
                if (success) {
                    setToastMessage("Promotion was deleted.");
                    loadPromotions();
                } else {
                    setToastMessage("Delete of promotion failed.");
                }
            });
        }
    }

    function addNewClick(): void {
        let newProm = new Promotion();
        newProm.code = "";
        newProm.description = "";
        newProm.type = "Fixed";
        newProm.amountDiscount = 5;
        newProm.numberOfBidPeriods = 1;
        newProm.activeStartDate = new Date();
        newProm.activeEndDate = new Date();

        rowSelected(newProm);
    }

    function closeEditor(): void {
        setSelectedPromotion(null);
        setEditPromotion(null);
    }

    return (
        <React.Fragment>
            <div className="main-container">
                <div className="row pad-top-10 pad-left-5">
                    <div className="col-md-5 fs-3">Promotions</div>
                    {!editPromotion && !selectedPromotion &&
                        <div className="col promotions-addbutton">
                            <button className="btn btn-outline-primary" onClick={addNewClick}>Add Promotion</button>
                        </div>}
                </div>
                {!!editPromotion && !!selectedPromotion &&
                    <React.Fragment key={"prom-edit-" + editPromotion.promotionId}>
                        <div className="row pad-top-10 pad-left-5">
                            <div className="col-md-1">
                                <label htmlFor="promotionCode" className="form-label">Code</label>
                                <input className="form-control" id="promotionCode" defaultValue={selectedPromotion.code}
                                    maxLength={24} autoComplete={"off"}
                                    onChange={(e) => {
                                        editPromotion.code = e.target.value;
                                        checkPromotionHasChanges();
                                    }}></input>
                            </div>
                            <div className="col-md-2">
                                <label htmlFor="promotionDesc" className="form-label">Description</label>
                                <input className="form-control" id="promotionDesc" defaultValue={selectedPromotion.description}
                                    maxLength={255} autoComplete={"off"}
                                    onChange={(e) => {
                                        editPromotion.description = e.target.value;
                                        checkPromotionHasChanges();
                                    }}></input>
                            </div>
                            <div className="col-md-1">
                                <label htmlFor="promotionType" className="form-label">Discount Type</label>
                                <select className="form-select" id="promotionType" defaultValue={selectedPromotion.type}
                                    onChange={(e) => {
                                        editPromotion.type = e.target.value;
                                        checkPromotionHasChanges();
                                    }} >
                                    <option value={"Fixed"}>Fixed</option>
                                    <option value={"Percent"}>Percent</option>
                                </select>
                            </div>
                            <div className="col-md-1" key={"proamt-" + typeSelected}>
                                <label htmlFor="promotionAmount" className="form-label">{editPromotion.type === "Fixed" ? "Dollar Amount" : "Percent Amount"}</label>
                                <div className="input-group">
                                    <span className="input-group-text">{editPromotion.type === "Fixed" ? company.currency : "%"}</span>
                                    <input type="number" className="form-control md-1 promotions-amount-input"
                                        id="promotionAmount" defaultValue={selectedPromotion.amountDiscount}
                                        autoComplete={"off"}
                                        onChange={(e) => {
                                            editPromotion.amountDiscount = parseInt(e.target.value);
                                            checkPromotionHasChanges();
                                        }}></input>
                                    {editPromotion.type === "Fixed" && <span className="input-group-text">.00</span>}
                                </div>
                            </div>
                            <div className="col-md-1">
                                <label htmlFor="promotionMonths" className="form-label">Months</label>
                                <input type="number" className="form-control" id="promotionMonths" defaultValue={selectedPromotion.numberOfBidPeriods}
                                    maxLength={255} autoComplete={"off"}
                                    onChange={(e) => {
                                        editPromotion.numberOfBidPeriods = parseInt(e.target.value);
                                        checkPromotionHasChanges();
                                    }}></input>
                            </div>
                        </div>
                        <div className="row pad-top-10 pad-left-5">
                            <div className="col-md-1">
                                <label htmlFor="promotionStart" className="form-label">Active Start</label>
                                <input type="date" className="form-control" id="promotionStart"
                                    defaultValue={`${selectedPromotion.activeStartDate}`}
                                    onChange={(e) => {
                                        editPromotion.activeStartDate = fromIsoDate(e.target.value);
                                        checkPromotionHasChanges();
                                    }}></input>
                            </div>
                            <div className="col-md-1">
                                <label htmlFor="promotionEnd" className="form-label">ActiveEnd</label>
                                <input type="date" className="form-control" id="promotionEnd"
                                    defaultValue={`${selectedPromotion.activeEndDate}`}
                                    onChange={(e) => {
                                        editPromotion.activeEndDate = fromIsoDate(e.target.value);
                                        checkPromotionHasChanges();
                                    }}></input>
                            </div>
                            <div className={!selectedPromotion.promotionId ? "col-md-4 promotions-savebutton" : "col-md-1 promotions-savebutton"}>
                                <button disabled={!anyChanges} className="btn btn-primary"
                                    onClick={saveChanges}>Save Changes</button>
                            </div>
                            {!!selectedPromotion.promotionId &&
                                <div className="col-md-3 promotions-savebutton">
                                    <ButtonConfirm cssClass="btn btn-outline-danger col-md-2" confirmTitle="Delete Promotion" confirmMessage={`Delete Promotion '${selectedPromotion.code}'?`}
                                        buttonText="Delete" onConfirm={deletePromotionClick} enabled={true} buttonSize={ButtonConfirmSize.Normal} />
                                </div>}
                            <div className="col-md-1 promotions-closebutton">
                                <CloseButton onClick={closeEditor} />
                            </div>
                        </div>
                    </React.Fragment>}
                <div className="promotions-table-wrapper">
                    <CclDataTable
                        name={"profilesTable"}
                        tableData={promotions || []}
                        columnDefinitions={bplColumnDefs}
                        onRowSelect={rowSelected}
                        rowSelect={true}
                        onButtonClick={null} />
                </div>
            </div>
            <Toaster title="Promotions" subtitle="Edit" message={toastMessage} key={"promotionstoast"} />
        </React.Fragment>
    )
}
